import { useEffect, useState } from "react";
import type { BrandingInfo } from "@platform/contracts";
import { api } from "../api";
import { resolvedTheme } from "../theme";

const EMPTY: BrandingInfo = { logoVersion: null, designerLightVersion: null, designerLightUrl: null, designerDarkVersion: null, designerDarkUrl: null, designerLinkUrl: null };
const CACHE_KEY = "talahwa-branding";

/** The branding record is fetched, so the first paint has nothing to draw. Without
 *  a remembered copy the default mark appears and is then replaced once the
 *  request lands — a visible flash of the wrong logo on every page load. The last
 *  known record is kept in localStorage and used for that first paint instead. */
function readCache(): BrandingInfo | undefined {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<BrandingInfo>;
    return { ...EMPTY, ...parsed };
  } catch { return undefined; }
}
function writeCache(info: BrandingInfo): void {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(info)); } catch { /* private mode or full quota: the fetch still works */ }
}

let cached: BrandingInfo | undefined = readCache();
let settled = cached !== undefined;
const subscribers = new Set<(branding: BrandingInfo) => void>();
function publish(info: BrandingInfo): void { cached = info; settled = true; subscribers.forEach((notify) => notify(info)); }
function load(): void {
  api.branding().then((info) => { writeCache(info); publish(info); }).catch(() => publish(cached ?? EMPTY));
}
export function refreshBranding(): void { load(); }

/** One shared fetch: the mark, the loading screen and the login credit all read
 *  the same branding record rather than each requesting it. `settled` reports
 *  whether anything is known yet, so a first-ever visit can hold back rather
 *  than guess. */
export function useBranding(): { branding: BrandingInfo; settled: boolean } {
  const [state, setState] = useState<{ branding: BrandingInfo; settled: boolean }>(() => ({ branding: cached ?? EMPTY, settled }));
  useEffect(() => {
    const notify = (info: BrandingInfo) => setState({ branding: info, settled: true });
    subscribers.add(notify);
    load();
    return () => { subscribers.delete(notify); };
  }, []);
  return state;
}

/** `hero` is the login screen treatment: a large logo standing on its own. The
 *  wordmark still appears when no logo is configured — otherwise the sign-in page
 *  would carry nothing but an abstract default mark and no name at all. */
export function BrandMark({ hero = false }: { hero?: boolean }) {
  const { branding, settled } = useBranding();
  // A cached version can outlive the logo it points at; the placeholder keeps the
  // row from jumping while a stale one 404s.
  const [failed, setFailed] = useState(false);
  const logoVersion = failed ? null : branding.logoVersion;
  useEffect(() => { setFailed(false); }, [branding.logoVersion]);
  const logoOnly = hero && Boolean(logoVersion);
  return <div className={logoOnly ? "brand brand-hero" : "brand"} aria-label="Tal-Ahwa home">
    {logoVersion
      ? <img className="brand-logo" src={api.brandingLogoUrl(logoVersion)} alt={logoOnly ? "Tal-Ahwa" : ""} onError={() => setFailed(true)} />
      : settled
        ? <span className="brand-mark" aria-hidden="true"><i /><i /><i /></span>
        : <span className="brand-mark-placeholder" aria-hidden="true" />}
    {!logoOnly && <span className="brand-wordmark">Tal-Ahwa</span>}
  </div>;
}

/** Shown on the login screen only. The logo is optional — the credit line stands
 *  on its own until a Super Admin points at one — and follows the active theme,
 *  falling back to whichever variant exists when only one is configured. */
export function DesignerCredit() {
  const { branding, settled } = useBranding();
  const [theme, setTheme] = useState<"light" | "dark">(resolvedTheme);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(resolvedTheme()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    setTheme(resolvedTheme());
    return () => observer.disconnect();
  }, []);
  const version = theme === "dark"
    ? branding.designerDarkVersion ?? branding.designerLightVersion
    : branding.designerLightVersion ?? branding.designerDarkVersion;
  useEffect(() => { setFailed(false); }, [version]);
  if (!settled) return <div className="designer-credit" aria-hidden="true"><span>Designed by</span><span className="designer-logo-placeholder" /></div>;
  const mark = version && !failed
    ? <img className="designer-logo" src={api.designerLogoUrl(theme, version)} alt="Outnet" onError={() => setFailed(true)} />
    : <strong>Outnet</strong>;
  return <div className="designer-credit">
    <span>Designed by</span>
    {branding.designerLinkUrl
      ? <a className="designer-link" href={branding.designerLinkUrl} target="_blank" rel="noopener noreferrer">{mark}</a>
      : mark}
  </div>;
}
